import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import useIsMobile from '../../../../helpers/useIsMobile'
import AnDateTimePicker from '../../../../components/DateTime/DateTimePicker'
import WitnessPicker from '../../../../components/Medicines/WitnessPicker'
import {
  emptyWitness,
  isWitnessComplete,
  toWitnessPayload,
} from '../../../../helpers/medicineWitness'
import { apiError } from './preparedHelpers'

// Draw a medicine up in advance from a bag (HEMS RSI/arrest drugs carried
// in a leg pocket). The chosen stock items leave the bag and the syringe is
// held by the signed-in clinician until it is given from an ePCR, handed
// over or discarded. POST /medsmx/prepared.
const DrawUpDialog = ({ open, onClose, bagID, bagLabel, onDrawn }) => {
  const { setToast } = useContext(GlobalContext)
  const isPhone = useIsMobile('sm')
  const [contents, setContents] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [medicineID, setMedicineID] = useState('')
  const [selected, setSelected] = useState([])
  const [dose, setDose] = useState('')
  const [volumeMl, setVolumeMl] = useState('')
  const [concentration, setConcentration] = useState('')
  const [label, setLabel] = useState('')
  const [expiresAt, setExpiresAt] = useState(null)
  const [witness, setWitness] = useState(emptyWitness())
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open || !bagID) return
    setMedicineID('')
    setSelected([])
    setDose('')
    setVolumeMl('')
    setConcentration('')
    setLabel('')
    setExpiresAt(null)
    setWitness(emptyWitness())
    setError('')
    setLoadError(false)
    setLoading(true)
    API.get(`/medsmx/bag-contents?id=${bagID}`)
      .then((res) => {
        if (res.status === 200) setContents(res.data?.contents ?? [])
        else setLoadError(true)
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }, [open, bagID])

  // Live stock grouped by medicine, soonest expiry first within each
  const medicines = useMemo(() => {
    const map = new Map()
    contents.forEach((item) => {
      const id = item.medicine?._id
      if (!id) return
      if (!map.has(id)) map.set(id, { medicine: item.medicine, items: [] })
      map.get(id).items.push(item)
    })
    map.forEach((group) =>
      group.items.sort(
        (a, b) => new Date(a.expiry || 0) - new Date(b.expiry || 0),
      ),
    )
    return [...map.values()].sort((a, b) =>
      (a.medicine.title ?? '').localeCompare(b.medicine.title ?? ''),
    )
  }, [contents])

  const group = medicines.find((g) => g.medicine._id === medicineID)
  const isControlled = !!group?.medicine?.isControlled

  const chooseMedicine = (id) => {
    setMedicineID(id)
    const first = medicines.find((g) => g.medicine._id === id)?.items?.[0]
    setSelected(first ? [first._id] : [])
    const description = medicines.find((g) => g.medicine._id === id)?.medicine
    setConcentration(description?.constitution ?? '')
  }

  const toggleItem = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )

  const canSubmit =
    !!medicineID &&
    selected.length > 0 &&
    !!dose.trim() &&
    (!isControlled || isWitnessComplete(witness)) &&
    !submitting

  const handleSubmit = () => {
    setError('')
    if (expiresAt && moment(expiresAt).isBefore(moment())) {
      setError('The expiry must be in the future')
      return
    }
    setSubmitting(true)
    API.post('/medsmx/prepared', {
      location: bagID,
      medicines: selected,
      dose: dose.trim(),
      volumeMl: volumeMl === '' ? undefined : volumeMl,
      concentration: concentration.trim() || undefined,
      label: label.trim() || undefined,
      expiresAt: expiresAt || undefined,
      witness: toWitnessPayload(witness),
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({
            message: `${res.data?.medicineName ?? 'Medicine'} drawn up - it is now held by you`,
          })
          onDrawn?.(res.data)
          onClose()
        } else {
          setError(apiError(res, 'Unable to record the draw-up'))
        }
      })
      .catch(() => setError('Unable to reach AmbuNet. Please try again.'))
      .finally(() => setSubmitting(false))
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isPhone}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        Draw up a medicine
        {bagLabel && (
          <Typography variant="body2" color="text.secondary">
            From {bagLabel}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <LinearProgress sx={{ my: 2 }} />
        ) : loadError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            Unable to load this bag&apos;s contents.
          </Alert>
        ) : medicines.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            This bag has no live stock to draw up.
          </Alert>
        ) : (
          <Stack gap={2} pt={1}>
            <Alert severity="info" variant="outlined" sx={{ py: 0.25 }}>
              The stock you pick leaves the bag and is held by you until it
              is given from an ePCR, handed to a colleague or discarded.
            </Alert>
            <FormControl fullWidth size="small">
              <InputLabel id="drawup-medicine">Medicine</InputLabel>
              <Select
                labelId="drawup-medicine"
                label="Medicine"
                value={medicineID}
                onChange={(e) => chooseMedicine(e.target.value)}
              >
                {medicines.map(({ medicine, items }) => (
                  <MenuItem key={medicine._id} value={medicine._id}>
                    {medicine.title}
                    {medicine.constitution ? ` - ${medicine.constitution}` : ''}
                    {` (${items.length} in bag)`}
                    {medicine.isControlled ? ' · CD' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {group && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Stock items drawn up
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tick every ampoule or vial used for this syringe.
                </Typography>
                <Stack
                  sx={{
                    mt: 0.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    maxHeight: 220,
                    overflowY: 'auto',
                  }}
                >
                  {group.items.map((item) => {
                    const expired =
                      item.expiry && moment(item.expiry).isBefore(moment())
                    return (
                      <Stack
                        key={item._id}
                        direction="row"
                        alignItems="center"
                        gap={1}
                        onClick={() => toggleItem(item._id)}
                        sx={{
                          px: 1,
                          py: 0.25,
                          cursor: 'pointer',
                          borderBottom: 1,
                          borderColor: 'divider',
                          '&:last-of-type': { borderBottom: 0 },
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selected.includes(item._id)}
                          onChange={() => toggleItem(item._id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Typography variant="body2" flex={1} minWidth={0}>
                          Batch {item.batchNumber || '—'}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={expired ? 'error' : 'default'}
                          label={
                            item.expiry
                              ? `Exp ${moment(item.expiry).format('DD/MM/YYYY')}`
                              : 'No expiry'
                          }
                          sx={{ borderRadius: 1 }}
                        />
                      </Stack>
                    )
                  })}
                </Stack>
              </Box>
            )}

            {group && (
              <>
                <TextField
                  label="Dose drawn up"
                  placeholder="e.g. 200mg in 20ml"
                  size="small"
                  required
                  value={dose}
                  onChange={(e) => setDose(e.target.value)}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                  <TextField
                    label="Volume"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: 0.1 }}
                    value={volumeMl}
                    onChange={(e) => setVolumeMl(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">ml</InputAdornment>
                      ),
                    }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Concentration"
                    placeholder="e.g. 10mg/ml"
                    size="small"
                    value={concentration}
                    onChange={(e) => setConcentration(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <TextField
                  label="Syringe label (optional)"
                  placeholder="e.g. RSI - ketamine"
                  size="small"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
                <AnDateTimePicker
                  label="Expire earlier (optional)"
                  value={expiresAt}
                  onChange={setExpiresAt}
                  size="small"
                  fullWidth
                  disablePast
                  helperText="Leave blank for your organisation's default. It can only be shortened, never extended past the stock expiry."
                />
                {isControlled && (
                  <Stack gap={1.5}>
                    <Alert severity="warning" variant="outlined" sx={{ py: 0.25 }}>
                      Controlled drug - drawing up must be witnessed by someone
                      else.
                    </Alert>
                    <WitnessPicker value={witness} onChange={setWitness} />
                  </Stack>
                )}
              </>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          Draw up
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DrawUpDialog
